import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, Upload, FileText, X, Plus, Eye, EyeOff, Download, Camera } from "lucide-react";

function SkillsEditor({ value, onChange }) {
    const [v, setV] = useState("");
    const add = (s) => {
        const x = s.trim();
        if (!x) return;
        if (!(value || []).includes(x)) onChange([...(value || []), x]);
        setV("");
    };
    return (
        <div>
            <div className="flex gap-2">
                <Input
                    value={v}
                    onChange={(e) => setV(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(v); } }}
                    placeholder="e.g. Python — press Enter to add"
                    className="rounded-none h-10"
                    data-testid="profile-skill-input"
                />
                <Button type="button" variant="outline" className="rounded-none h-10" onClick={() => add(v)} data-testid="profile-skill-add">
                    <Plus className="w-4 h-4" />
                </Button>
            </div>
            {(value || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                    {(value || []).map((s) => (
                        <span key={s} className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-border bg-white text-xs font-medium">
                            {s}
                            <button type="button" onClick={() => onChange((value || []).filter((x) => x !== s))} className="text-muted-foreground hover:text-destructive" data-testid={`profile-skill-remove-${s}`}>
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function SeekerProfile() {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [completeness, setCompleteness] = useState(null);
    const [photoVersion, setPhotoVersion] = useState(0);
    const [photoLoaded, setPhotoLoaded] = useState(false);
    const fileRef = useRef(null);
    const photoRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const [{ data }, c] = await Promise.all([
                api.get("/seeker/profile"),
                api.get("/seeker/profile-completeness").catch(() => ({ data: null })),
            ]);
            setProfile(data);
            setCompleteness(c?.data || null);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

    const save = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        try {
            const body = {
                name: profile.name, phone: profile.phone, location: profile.location,
                current_title: profile.current_title, current_company: profile.current_company,
                linkedin_url: profile.linkedin_url, ai_summary: profile.ai_summary,
                skills: profile.skills,
            };
            const { data } = await api.patch("/seeker/profile", body);
            setProfile(data);
            toast.success("Profile updated everywhere");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const onFile = async (file) => {
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/seeker/profile/resume", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
            setProfile(data);
            toast.success("Resume re-parsed and profile updated");
            // Refresh completeness
            api.get("/seeker/profile-completeness").then((r) => setCompleteness(r.data)).catch(() => {});
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
    };

    const onPhoto = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) return toast.error("Please pick an image file");
        if (file.size > 2 * 1024 * 1024) return toast.error("Max photo size is 2 MB");
        setUploadingPhoto(true);
        const fd = new FormData();
        fd.append("photo", file);
        try {
            await api.post("/seeker/profile-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPhotoLoaded(false);
            setPhotoVersion((v) => v + 1);
            toast.success("Profile photo updated");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setUploadingPhoto(false); if (photoRef.current) photoRef.current.value = ""; }
    };

    const toggleVisibility = async (next) => {
        try {
            await api.patch("/seeker/visibility", { open_to_opportunities: next });
            setProfile((p) => ({ ...p, open_to_opportunities: next }));
            toast.success(next ? "You're visible to recruiters" : "You're hidden from recruiter search");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const exportData = async () => {
        try {
            const { data } = await api.get("/seeker/export-data", { timeout: 30000 });
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const ts = new Date().toISOString().slice(0, 10);
            a.download = `portalwiz-my-data-${ts}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Your data has been downloaded");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    if (loading) return <div className="p-8 text-sm text-muted-foreground font-mono">Loading...</div>;
    if (!profile) return null;

    const hasCandidateRows = (profile.linked_candidate_ids || []).length > 0;
    const seekerUid = profile.id || profile.seeker?.id;
    const photoUrl = seekerUid ? `${process.env.REACT_APP_BACKEND_URL}/api/seeker/profile-photo/${seekerUid}?v=${photoVersion}` : null;
    const initials = (profile.name || profile.email || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
    const pct = completeness?.pct || 0;    return (
        <div className="max-w-5xl mx-auto p-3 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
            {/* Hero — photo + name + completion */}
            <div className="bg-gradient-to-br from-white via-white to-secondary/40 border border-border rounded-2xl p-5 sm:p-8">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
                    <div className="relative shrink-0">
                        <button
                            type="button"
                            onClick={() => photoRef.current?.click()}
                            className="group relative w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden bg-secondary border-2 border-border hover:border-ikb transition-colors"
                            data-testid="profile-photo-pick"
                        >
                            {photoUrl ? (
                                <img
                                    src={photoUrl}
                                    alt={profile.name || "You"}
                                    className="w-full h-full object-cover relative z-10"
                                    onLoad={() => setPhotoLoaded(true)}
                                    onError={(e) => { e.currentTarget.style.display = "none"; setPhotoLoaded(false); }}
                                    data-testid="profile-photo-img"
                                />
                            ) : null}
                            {!photoLoaded && (
                                <div className="absolute inset-0 flex items-center justify-center font-display font-bold text-2xl sm:text-3xl text-muted-foreground pointer-events-none">
                                    {initials}
                                </div>
                            )}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white z-20">
                                <Camera className="w-6 h-6" />
                            </div>
                        </button>
                        <input ref={photoRef} type="file" accept="image/*" hidden onChange={(e) => onPhoto(e.target.files?.[0])} data-testid="profile-photo-input" />
                        {uploadingPhoto && <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center text-xs text-white">Uploading…</div>}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[11px] uppercase tracking-widest font-mono text-ikb font-medium">Your profile</div>
                        <h1 className="font-display font-bold text-2xl sm:text-4xl mt-1.5 tracking-tight">{profile.name || "Build your profile"}</h1>
                        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                            One profile. Visible to every company you've applied to.
                            {hasCandidateRows ? ` · Synced across ${profile.linked_company_count} compan${profile.linked_company_count === 1 ? "y" : "ies"}.` : ""}
                        </p>
                        {completeness && (
                            <div className="mt-4" data-testid="profile-completeness">
                                <div className="flex items-center justify-between text-xs mb-1.5">
                                    <span className="font-medium">Profile strength</span>
                                    <span className={`font-mono font-bold ${pct >= 80 ? "text-emerald-600" : pct >= 50 ? "text-amber-600" : "text-rose-600"}`}>{pct}%</span>
                                </div>
                                <div className="h-2 bg-secondary rounded-full overflow-hidden">
                                    <div className={`h-full transition-all duration-500 ${pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${pct}%` }} />
                                </div>
                                {(completeness.missing || []).length > 0 && (
                                    <div className="text-[11px] text-muted-foreground mt-1.5">
                                        Add: {(completeness.missing || []).slice(0, 3).map((m) => m.label).join(" · ")}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {profile.resume_filename && (
                <div className="text-xs font-mono text-muted-foreground inline-flex items-center gap-2 border border-border px-3 py-2 bg-white rounded-md" data-testid="profile-resume-current">
                    <FileText className="w-3.5 h-3.5" /> {profile.resume_filename}
                </div>
            )}

            {!hasCandidateRows && !profile.resume_filename && (
                <div className="border border-amber-300 bg-amber-50 text-amber-900 p-4 sm:p-5 text-sm rounded-lg">
                    <strong>Upload your resume</strong> below so we can parse your experience, skills and education — then you'll be able to apply to any role with one tap.
                </div>
            )}

            <form onSubmit={save} className="space-y-8">
                <section className="border border-border bg-white p-8 space-y-6">
                    <h2 className="font-display font-bold text-xl">Basics</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Full name</Label>
                            <Input value={profile.name || ""} onChange={(e) => set("name", e.target.value)} className="rounded-none mt-2 h-11" data-testid="profile-name" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Email (locked)</Label>
                            <Input value={profile.email || ""} disabled className="rounded-none mt-2 h-11 bg-secondary" data-testid="profile-email" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Phone</Label>
                            <Input value={profile.phone || ""} onChange={(e) => set("phone", e.target.value)} className="rounded-none mt-2 h-11" data-testid="profile-phone" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Location</Label>
                            <Input value={profile.location || ""} onChange={(e) => set("location", e.target.value)} className="rounded-none mt-2 h-11" data-testid="profile-location" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Current title</Label>
                            <Input value={profile.current_title || ""} onChange={(e) => set("current_title", e.target.value)} className="rounded-none mt-2 h-11" data-testid="profile-title" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Current company</Label>
                            <Input value={profile.current_company || ""} onChange={(e) => set("current_company", e.target.value)} className="rounded-none mt-2 h-11" data-testid="profile-company" />
                        </div>
                        <div className="md:col-span-2">
                            <Label className="text-xs uppercase tracking-widest font-bold">LinkedIn URL</Label>
                            <Input value={profile.linkedin_url || ""} onChange={(e) => set("linkedin_url", e.target.value)} className="rounded-none mt-2 h-11" placeholder="https://linkedin.com/in/..." data-testid="profile-linkedin" />
                        </div>
                        <div className="md:col-span-2">
                            <Label className="text-xs uppercase tracking-widest font-bold">Headline / summary</Label>
                            <Textarea
                                value={profile.ai_summary || ""}
                                onChange={(e) => set("ai_summary", e.target.value)}
                                className="rounded-none mt-2 min-h-[120px]"
                                placeholder="A few lines on what you do and what you're looking for next."
                                data-testid="profile-summary"
                            />
                        </div>
                    </div>
                </section>

                <section className="border border-border bg-white p-8 space-y-6">
                    <h2 className="font-display font-bold text-xl">Skills</h2>
                    <SkillsEditor value={profile.skills || []} onChange={(v) => set("skills", v)} />
                </section>

                <section className="border border-border bg-white p-8 space-y-4">
                    <div>
                        <h2 className="font-display font-bold text-xl">Resume</h2>
                        <p className="text-sm text-muted-foreground mt-1">Re-uploading runs the resume through our AI parser and updates everything (experience, education, certifications, suggested skills).</p>
                    </div>
                    <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.txt" hidden onChange={(e) => onFile(e.target.files?.[0])} data-testid="profile-resume-input" />
                    <Button
                        type="button"
                        variant="outline"
                        className="rounded-none h-10"
                        disabled={uploading}
                        onClick={() => fileRef.current?.click()}
                        data-testid="profile-resume-upload"
                    >
                        <Upload className="w-4 h-4 mr-2" />
                        {uploading ? "Parsing..." : profile.resume_filename ? "Replace resume" : "Upload resume"}
                    </Button>
                </section>

                {((profile.experience || []).length > 0 || (profile.certifications || []).length > 0) && (
                    <section className="border border-border bg-white p-8 space-y-6">
                        <h2 className="font-display font-bold text-xl">Imported from resume</h2>
                        {(profile.experience || []).length > 0 && (
                            <div>
                                <Label className="text-xs uppercase tracking-widest font-bold mb-2 block">Experience</Label>
                                <ul className="space-y-3">
                                    {(profile.experience || []).map((x, i) => (
                                        <li key={i} className="text-sm border border-border bg-secondary/30 p-3">
                                            <div className="font-medium">{x.title || x.role || "—"}{x.company ? ` · ${x.company}` : ""}</div>
                                            <div className="text-xs text-muted-foreground font-mono">{x.start || ""} – {x.end || "Present"}{x.location ? ` · ${x.location}` : ""}</div>
                                            {x.summary && <div className="text-xs text-muted-foreground mt-1">{x.summary}</div>}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {(profile.certifications || []).length > 0 && (
                            <div>
                                <Label className="text-xs uppercase tracking-widest font-bold mb-2 block">Certifications</Label>
                                <ul className="flex flex-wrap gap-2">
                                    {(profile.certifications || []).map((c, i) => (
                                        <li key={i} className="text-xs px-2.5 py-1 border border-border bg-white">
                                            {typeof c === "string" ? c : (c.name || c.title || JSON.stringify(c))}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </section>
                )}

                <section className="border border-border bg-white p-8 space-y-4">
                    <div>
                        <h2 className="font-display font-bold text-xl">Visibility</h2>
                        <p className="text-sm text-muted-foreground mt-1">Control whether recruiters can find your profile in their candidate search.</p>
                    </div>
                    <div className="flex items-center justify-between gap-4 border border-border p-4 bg-secondary/30">
                        <div className="flex items-start gap-3">
                            {profile.open_to_opportunities !== false
                                ? <Eye className="w-5 h-5 text-emerald-600 mt-0.5" />
                                : <EyeOff className="w-5 h-5 text-muted-foreground mt-0.5" />}
                            <div>
                                <div className="font-display font-bold text-sm" data-testid="visibility-label">
                                    {profile.open_to_opportunities !== false ? "Open to opportunities" : "Hidden from recruiters"}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                    {profile.open_to_opportunities !== false
                                        ? "Recruiters across the platform can discover and contact you."
                                        : "Your profile won't appear in recruiter search. You can still apply to jobs directly."}
                                </div>
                            </div>
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            className="rounded-none h-10 shrink-0"
                            onClick={() => toggleVisibility(!(profile.open_to_opportunities !== false))}
                            data-testid="visibility-toggle"
                        >
                            {profile.open_to_opportunities !== false ? "Hide me" : "Make me visible"}
                        </Button>
                    </div>
                </section>

                <section className="border border-border bg-white p-8 space-y-4">
                    <div>
                        <h2 className="font-display font-bold text-xl">Your data</h2>
                        <p className="text-sm text-muted-foreground mt-1">Download a full copy of everything we have on you — profile, applications, saved jobs, alerts, notifications, and support tickets.</p>
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        className="rounded-none h-10"
                        onClick={exportData}
                        data-testid="export-data-btn"
                    >
                        <Download className="w-4 h-4 mr-2" /> Export my data (JSON)
                    </Button>
                </section>

                <div className="flex justify-end">
                    <Button type="submit" disabled={saving} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-11 px-8" data-testid="profile-save">
                        <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save profile"}
                    </Button>
                </div>
            </form>
        </div>
    );
}
